import React from 'react';
import { ItemWithTranslation, SportsCacheFiltroTestualeDto } from 'types/swagger';
import { RadioButton, RadioOptionType } from 'components/inputs/radioButton';
import { SimpleCheckbox, SimpleCheckboxType } from 'components/inputs/simpleCheckbox';
import { ToggleCheckBox, ToggleCheckBoxType } from 'components/inputs/toggleCheckBox';

import classNames from 'classnames';
import { selectIsScommessaLoading } from 'features/sport';
import styles from './ScommesseFilterTertiary.module.scss';
import { useTranslation } from 'hooks/useLingUI';
import { useTypedSelector } from 'lib/centralStore';

/* eslint-disable no-unused-vars */
export enum ItemType {
  radio = 'radio',
  toggle = 'toggle',
  checkbox = 'checkbox',
}
/* eslint-enable no-unused-vars */

export type ItemTypes = keyof typeof ItemType;

// kept SportsCacheFiltroTestualeDto from original spor's data-model in order to persist nullable definitions for attributes
export type BaseItemFilter = Partial<SportsCacheFiltroTestualeDto>;

export type FilterTertiaryItemType = RadioOptionType | SimpleCheckboxType | ToggleCheckBoxType;

export type ItemValueType = Partial<SportsCacheFiltroTestualeDto | FilterTertiaryItemType>;

export type ScommesseFilterTertiaryProps = ItemWithTranslation<{ title?: string }> & {
  items: Array<ItemValueType>;
  type: ItemTypes;
  className?: string;
  checkedItem?: boolean;
  defaultChecked?: string;
  classNameTitle?: string;
  classNameContainer?: string;
  clickAction?: Function;
};

export const ScommesseFilterTertiary = ({
  type,
  items,
  title,
  className,
  titleTrKey,
  clickAction,
  checkedItem,
  defaultChecked,
  classNameTitle,
  classNameContainer,
}: ScommesseFilterTertiaryProps) => {
  const { t } = useTranslation();

  // should be on buttons, but here is most centralized
  const isScommessaLoading = useTypedSelector(selectIsScommessaLoading);

  const renderContent = () => {
    switch (type) {
      case ItemType.radio: {
        return (
          <RadioButton
            name="scommessa-radio"
            options={items as Array<RadioOptionType>}
            defaultValue={defaultChecked!}
            onChange={(e) => {
              clickAction!(e.currentTarget);
            }}
            className={styles.radioWrapper}
          />
        );
      }
      case ItemType.checkbox: {
        return (
          <SimpleCheckboxGroup
            items={items as Array<SimpleCheckboxType>}
            checkedItem={checkedItem!}
            handleChecked={clickAction!}
          />
        );
      }
      case ItemType.toggle: {
        return <ToggleCheckboxGroup items={items as Array<ToggleCheckBoxType>} onClick={clickAction! as () => void} />;
      }
      default:
        return null;
    }
  };

  return (
    <div className={classNames(styles.container, { [styles.disabled]: isScommessaLoading }, classNameContainer ?? '')}>
      {title && <span className={classNames(styles.title, classNameTitle)}>{t(titleTrKey, title)}</span>}
      <div className={classNames(styles.wrapper, className)}>{renderContent()}</div>
    </div>
  );
};

type SimpleCheckboxGroupProps = {
  items: SimpleCheckboxType[];
  checkedItem: boolean;
  handleChecked?: Function;
};

const SimpleCheckboxGroup = ({ items, checkedItem, handleChecked }: SimpleCheckboxGroupProps) => {
  const onFilterChangeHandler = (e: EventTarget & HTMLInputElement) => {
    if (handleChecked) handleChecked(e);
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <React.Fragment>
      {items?.map(({ value, label, labelTrKey }: FilterTertiaryItemType, index: number) => {
        return (
          <SimpleCheckbox
            key={`${value}_${index}`}
            value={value}
            label={label}
            labelTrKey={labelTrKey}
            checked={checkedItem}
            className={styles.checkbox}
            handleFilterChange={onFilterChangeHandler}
          />
        );
      })}
    </React.Fragment>
  );
};

type ToggleCheckboxGroupProps = {
  items: ToggleCheckBoxType[];
  onClick?: () => void;
};

const ToggleCheckboxGroup = ({ items, onClick }: ToggleCheckboxGroupProps) => {
  return (
    <React.Fragment>
      {items.map(({ value, label, labelTrKey, checked }: SimpleCheckboxType, index: number) => {
        return (
          <ToggleCheckBox
            key={`${value}_${index}`}
            value={value}
            label={label}
            labelTrKey={labelTrKey}
            isChecked={checked}
            handleFilterChange={onClick!}
          />
        );
      })}
    </React.Fragment>
  );
};